/**
 * Enum representing different stages of the application.
 * @enum {string}
 */
export enum Stages {
  ALPHA = 'alpha',
  BETA = 'beta',
  GAMMA = 'gamma',
  PROD = 'prod',
}

/**
 * Custom error class for API-related errors.
 * @extends Error
 */
export class ApiError extends Error {
  /** The HTTP status code associated with the error. */
  status: number;

  /**
   * Creates an instance of ApiError.
   * @param {number} status - The HTTP status code.
   * @param {string} message - The error message.
   */
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export const UI_PAGE_SIZE = 300;
export const MAX_PIVOT_API_ITERATIONS = 20;

export const CONTROL_APIS = {
  BASE_PATH: '/chub/v1/controls',
  ENDPOINTS: {
    DATA: (controlId: string) => `${CONTROL_APIS.BASE_PATH}/${controlId}/data`,
    GL_DATA: (controlId: string) => `${CONTROL_APIS.BASE_PATH}/${controlId}/gl-data`,
    METADATA: (controlId: string) => `${CONTROL_APIS.BASE_PATH}/${controlId}/metadata`,
    PIVOT_DATA: (controlId: string) => `${CONTROL_APIS.BASE_PATH}/${controlId}/pivotData`,
    DRILLDOWN_DATA: (controlId: string) => `${CONTROL_APIS.BASE_PATH}/${controlId}/drillDownData`,
    ANALYTICS_CHART_DATA: (controlId: string) =>
      `${CONTROL_APIS.BASE_PATH}/${controlId}/analyticsChartData`,
  },
  BATCH_SIZE: 1500,
  ERROR_MESSAGES: {
    DATA_FETCH_FAILED: 'Failed to fetch data. Please try again.',
    METADATA_FETCH_FAILED: 'Failed to fetch metadata. Please try refreshing the page.',
    UNAUTHORIZED: 'Unauthorized: Please check your credentials.',
    RESTRICTED_ACCESS:
      'Restricted Access: Please reach out to controllership-hub@amazon.com to request access.',
  },
} as const;

/**
 * Object containing API endpoint paths.
 * @type {Record<string, string>}
 */
export const ApiPaths: Record<string, string> = {
  getControls: '/GetControls',
  getControlAttributes: '/GetControlAttributes',
  getControlData: '/GetControlData',
  updateControlData: '/UpdateControlData',
  getUserPreferences: '/GetUserPreferences',
  updateUserPreferences: '/UpdateUserPreferences',
  previewDerivedUpdates: '/PreviewDerivedUpdates',
};

export const NavigationPaths: Record<string, string> = {
  summary: '/summary',
  historical: '/historical',
};

/**
 * Determines the current stage based on the hostname.
 * @returns {string} The current stage.
 */
export const getCurrentStage = (): string => {
  const hostname = window.location.hostname;
  return hostname.split('.')[0];
};

/**
 * Retrieves the base URL for API calls based on the given stage.
 * @param {string} stage - The current stage.
 * @returns {string} The base URL for API calls.
 */
export const getBaseUrl = (stage: string): string => {
  if (stage === 'localhost') {
    return `https://${'prod'}.chub-api.controllershiphub.finergy.amazon.dev`;
  }
  return `https://${stage}.chub-api.controllershiphub.finergy.amazon.dev`;
};
